// client/src/components/tax/PayeeForm.jsx
//
// /tax/payee/:token — public, W-9-style taxpayer form for a raffle winner
// whose prize must be reported on Form W-2G. The link is single-use and
// expires; the TIN is sent once over HTTPS, encrypted on the server, and
// never shown back — only its last four digits.
//
// API: /api/tax/payee/:token (server/routes/taxPayeeRoutes.js).

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPayeeForm, submitPayeeForm } from '../../services/taxApi';
import '../analytics/PerformancePanel.css';
import '../forms/Forms.css';
import './Tax.css';

/** Label + control + error, with the error OUTSIDE the label so it never becomes part of the field's name. */
function Field({ id, label, error, children }) {
  return (
    <div className="fo-field">
      <label htmlFor={`payee-${id}`}>{label}</label>
      {children}
      {error && <span className="tx-err" id={`payee-${id}-err`}>{error}</span>}
    </div>
  );
}

const EMPTY = { legalName: '', line1: '', line2: '', city: '', state: '', zip: '', tinType: 'ssn', tin: '', certify: false };

export default function PayeeForm() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState('loading'); // loading | form | done | invalid
  const [form, setForm] = useState(EMPTY);
  const [fields, setFields] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [last4, setLast4] = useState('');
  const [showTin, setShowTin] = useState(false);

  useEffect(() => {
    getPayeeForm(token)
      .then((r) => { setInfo(r); setState(r.completed ? 'done' : r.expired ? 'invalid' : 'form'); })
      .catch(() => setState('invalid'));
  }, [token]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setFields({});
    try {
      const r = await submitPayeeForm(token, {
        legalName: form.legalName,
        address: { line1: form.line1, line2: form.line2, city: form.city, state: form.state.toUpperCase(), zip: form.zip },
        tinType: form.tinType, tin: form.tin, certify: form.certify,
      });
      setForm(EMPTY); // drop the TIN from memory
      setLast4(r.tinLast4);
      setState('done');
    } catch (err) {
      setError(err.message || t('tax.payee.error', 'Could not save the form.'));
      setFields(err.fields || {});
      if (err.status === 409 || err.status === 410) setState(err.status === 409 ? 'done' : 'invalid');
    } finally {
      setBusy(false);
    }
  };

  const invalid = (k) => (fields[k] ? { 'aria-invalid': true, 'aria-describedby': `payee-${k}-err` } : {});

  return (
    <div className="tx-public">
      <div className="tx-public-inner">
        <section className="surface">
          <h1>{t('tax.payee.title', 'Taxpayer information for your prize')}</h1>
          {info && (
            <p className="perf-small">
              {t('tax.payee.intro', '{{org}} must report your prize from {{raffle}} to the IRS on Form W-2G and needs the information below before releasing it.', { org: info.organization, raffle: info.raffle })}
              {info.prize ? ` ${t('tax.payee.prize', 'Prize: {{prize}}.', { prize: info.prize })}` : ''}
            </p>
          )}
          {info && <div className="note warn tx-disclaimer" role="note">{t('tax.payee.disclaimer', 'Provided for your records. Not tax advice. Consult a tax professional. {{org}} is responsible for filing.', { org: info.organization })}</div>}
        </section>

        {state === 'loading' && <p>{t('common.loading', 'Loading…')}</p>}
        {state === 'invalid' && <div className="note warn" role="alert">{t('tax.payee.invalid', 'This link is not valid or has expired. Ask the organization to send a new one.')}</div>}
        {state === 'done' && (
          <div className="note info" role="status">
            {last4
              ? t('tax.payee.thanks', 'Thank you. Your information was saved securely (taxpayer ID ending {{last4}}).', { last4 })
              : t('tax.payee.already', 'This form has already been completed. Thank you.')}
          </div>
        )}

        {state === 'form' && (
          <form className="surface tx-form" onSubmit={submit} autoComplete="off" noValidate>
            <Field id="legalName" label={t('tax.payee.legalName', 'Full legal name (as shown on your tax return)')} error={fields.legalName}>
              <input id="payee-legalName" type="text" value={form.legalName} onChange={set('legalName')} autoComplete="name" required {...invalid('legalName')} />
            </Field>
            <Field id="line1" label={t('tax.settings.line1', 'Address')} error={fields.line1}>
              <input id="payee-line1" type="text" value={form.line1} onChange={set('line1')} autoComplete="address-line1" required {...invalid('line1')} />
            </Field>
            <Field id="line2" label={t('tax.settings.line2', 'Address line 2')}>
              <input id="payee-line2" type="text" value={form.line2} onChange={set('line2')} autoComplete="address-line2" />
            </Field>
            <div className="tx-row">
              <Field id="city" label={t('tax.settings.city', 'City')} error={fields.city}>
                <input id="payee-city" type="text" value={form.city} onChange={set('city')} autoComplete="address-level2" required {...invalid('city')} />
              </Field>
              <Field id="state" label={t('tax.settings.state', 'State')} error={fields.state}>
                <input id="payee-state" type="text" value={form.state} onChange={set('state')} autoComplete="address-level1" maxLength={2} required {...invalid('state')} />
              </Field>
              <Field id="zip" label={t('tax.settings.zip', 'ZIP')} error={fields.zip}>
                <input id="payee-zip" type="text" inputMode="numeric" value={form.zip} onChange={set('zip')} autoComplete="postal-code" maxLength={10} required {...invalid('zip')} />
              </Field>
            </div>
            <div className="tx-row">
              <Field id="tinType" label={t('tax.payee.tinType', 'Taxpayer ID type')} error={fields.tinType}>
                <select id="payee-tinType" value={form.tinType} onChange={set('tinType')}>
                  <option value="ssn">{t('tax.payee.ssn', 'Social Security number')}</option>
                  <option value="itin">{t('tax.payee.itin', 'ITIN')}</option>
                  <option value="ein">{t('tax.payee.ein', 'EIN (business)')}</option>
                </select>
              </Field>
              <Field id="tin" label={t('tax.payee.tin', 'Taxpayer ID number')} error={fields.tin}>
                <input id="payee-tin" type={showTin ? 'text' : 'password'} inputMode="numeric" value={form.tin} onChange={set('tin')} autoComplete="off" spellCheck={false} required {...invalid('tin')} />
              </Field>
            </div>
            <label className="tx-check"><input type="checkbox" checked={showTin} onChange={(e) => setShowTin(e.target.checked)} /> {t('tax.payee.show', 'Show number')}</label>
            <label className="tx-check">
              <input type="checkbox" checked={form.certify} onChange={set('certify')} {...invalid('certify')} />
              <span>{t('tax.payee.certify', 'Under penalties of perjury, I certify that the number shown is my correct taxpayer identification number, and that I am a U.S. citizen or other U.S. person.')}</span>
            </label>
            {fields.certify && <span className="tx-err" id="payee-certify-err">{fields.certify}</span>}
            <p className="tx-hint">{t('tax.payee.privacy', 'Your number is encrypted and used only to prepare your Form W-2G. Only the last 4 digits are ever displayed.')}</p>
            {error && <p className="tx-err" role="alert">{error}</p>}
            <div className="tx-actions">
              <button type="submit" className="lm-btn" disabled={busy}>{busy ? t('tax.payee.saving', 'Saving…') : t('tax.payee.submit', 'Submit securely')}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
